import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  TablePagination,
} from "@mui/material";
import { CirclePlus, Search } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getStocks,
  createStock,
  updateStock,
  deleteStock,
} from "../../api/Stock Management/api";
import usePermissions from "./hooks/usePermission";
import DataTable from "../../components/DataTable";
import StockForm from "../../components/shared/forms/StockForm";
import { AuthContext } from "../../contexts/AuthContext";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { extractErrorMessages } from "../../utils/errorHandler";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const StockManagement = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!loadingPermissions && can.viewStock()) {
      fetchStock();
    }
  }, [loadingPermissions]);

  const fetchStock = async () => {
    setLoading(true);
    try {
      const response = await getStocks({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const transformedData = response.data.map((stock) => {
          return {
            id: stock.id,
            product: stock.product,
            availableQuantity: stock.available_quantity,
            reservedQuantity: stock.reserved_quantity,
            servingWarehouse: stock.serving_warehouse,
            stockDate: stock.stock_date,
            stockRegion: stock.stock_region,
            totalQuantity: stock.total_quantity,
            status: stock.status,
            // Keep original data for form population
            original: stock,
          };
        });
        console.log("Stock loaded:", transformedData);
        setStock(transformedData);
        setTotal(response.total);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching Stock:", err);
      setError("Failed to load Stock. Please try again later.");
      setStock([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=> {
    fetchStock();
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transformStockData = (data) => {
    return {
      product: data.product,
      available_quantity: parseInt(data.available_quantity),
      reserved_quantity: parseInt(data.reserved_quantity),
      serving_warehouse: data.serving_warehouse,
      stock_date: data.stock_date,
      stock_region: data.stock_region,
      status: data.status,
    };
  };

  const handleCreateStock = async (data) => {
    try {
      // Validate required fields
      if (
        !data.product ||
        !data.available_quantity ||
        !data.reserved_quantity
      ) {
        setError("Please fill in all required fields");
        return;
      }

      const transformedData = transformStockData(data);
      await createStock(transformedData);
      showSuccessToast("Stock created successfully");
      setOpenModal(false);
      fetchStock();
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach(message => {
        showErrorToast(message);
      });
    }
  };

  const handleUpdateStock = async (id, data) => {
    try {
      // Validate required fields
      if (
        !data.product ||
        !data.available_quantity ||
        !data.reserved_quantity
      ) {
        setError("Please fill in all required fields");
        return;
      }

      const transformedData = transformStockData(data);
      await updateStock(id, transformedData);
      setOpenModal(false);
      setSelectedStock(null);
      fetchStock();
      showSuccessToast("Stock updated successfully");
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach(message => {
        showErrorToast(message);
      });
    }
  };

  const handleDeleteStock = async () => {
    if (!stockToDelete) return;

    try {
      await deleteStock(stockToDelete.id);
      setDeleteModalOpen(false);
      setStockToDelete(null);
      fetchStock();
      showSuccessToast("Stock deleted successfully");
    } catch (error) {
      console.error("Error deleting stock:", error);
      showErrorToast(
        error.response?.data?.message ||
          "Failed to delete stock. Please try again."
      );
    }
  };

  const handleSubmit = (data) => {
    if (selectedStock) {
      handleUpdateStock(selectedStock.id, data);
    } else {
      handleCreateStock(data);
    }
  };

  const filteredStock = stock.filter((stock) =>
    Object.values(stock).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "product", label: "Product" },
    { id: "availableQuantity", label: "Available Quantity" },
    { id: "reservedQuantity", label: "Reserved Quantity" },
    { id: "servingWarehouse", label: "Serving Warehouse" },
    { id: "stockDate", label: "Stock Date" },
    { id: "stockRegion", label: "Stock Region" },
    { id: "status", label: "Status" },
  ];

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Stock
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        {can.createStock() && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => {
              setSelectedStock(null);
              setOpenModal(true);
            }}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add Stock
          </Button>
        )}
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredStock}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalRows={total}
        onEdit={
          can.editStock()
            ? (rowData) => {
                setSelectedStock(rowData);
                setOpenModal(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        editTooltip="Edit"
        editDisabled={!can.editStock()}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      {(can.createStock() || can.editStock()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedStock(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <StockForm
              initialData={selectedStock?.original || null}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedStock(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}
      <Toast />
    </Box>
  );
};

export default StockManagement;